import { useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { Input, Modal as AntModal, Select, Spin, Switch } from 'antd';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import LinksButton from 'shared/design-system/components/links-button';
import NoData from 'shared/design-system/components/no-data-box';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';

export interface CrudField {
  key: string;
  label: string;
  required?: boolean;
  type?: 'text' | 'textarea' | 'boolean' | 'select';
  trueLabel?: string;
  falseLabel?: string;
  options?: { value: string | number; label: string }[];
  // Solo per filterFields: se valorizzato, il filtro cerca su questi key (OR) anziché sul singolo `key`.
  matchKeys?: string[];
  // Testo suggerito mostrato nell'input/select vuoto (form e filtro). Fallback: `label`.
  placeholder?: string;
  // Override opzionale per la resa del valore nella cella di tabella (es. campi derivati/annidati).
  // Se assente si usa la resa di default in base a `type`.
  render?: (item: any) => React.ReactNode;
}

interface Item {
  id?: number;
  [k: string]: any;
}

interface Props<T extends Item> {
  fetchAll: () => Promise<T[]>;
  save: (item: T) => Promise<T | undefined>;
  remove: (id: number) => Promise<void>;
  fields: CrudField[];
  filterFields?: CrudField[];
  emptyMessage?: string;
  addLabel?: string;
  fileName?: string;
  modalWidth?: number | string;
}

function SimpleCrudTab<T extends Item>({
  fetchAll,
  save,
  remove,
  fields,
  filterFields,
  emptyMessage,
  addLabel,
  fileName,
  modalWidth,
}: Props<T>) {
  const { t } = useTranslation();
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<T | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [formValues, setFormValues] = useState<Partial<T>>({});
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const load = () => {
    setLoading(true);
    fetchAll()
      .then((res) => setItems(res || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(undefined);
    setFormValues({});
    setShowForm(true);
  };

  const openEdit = (item: T) => {
    setEditing(item);
    setFormValues({ ...item });
    setShowForm(true);
  };

  const missingRequired = useMemo(() => {
    return fields.some((f) => {
      if (!f.required) return false;
      if (f.type === 'boolean') return formValues[f.key] == null;
      return !String(formValues[f.key] ?? '').trim();
    });
  }, [fields, formValues]);

  const renderCellValue = (item: T, f: CrudField) => {
    const v = item[f.key];
    if (f.type === 'boolean') return v ? f.trueLabel ?? t('Sì') : f.falseLabel ?? t('No');
    if (f.type === 'select') {
      const opt = f.options?.find((o) => o.value === v);
      return opt ? opt.label : v ?? '-';
    }
    return v ?? '-';
  };

  // Resa della cella: consente un `render` custom (es. valori derivati/annidati).
  const renderCell = (item: T, f: CrudField) => (f.render ? f.render(item) : renderCellValue(item, f));

  // Client-side filter
  const filteredItems = useMemo(() => {
    if (!filterFields?.length) return items;
    return items.filter((item) =>
      filterFields.every((f) => {
        const filterVal = filterValues[f.key];
        if (filterVal == null || filterVal === '') return true;
        if (f.type === 'boolean' || f.type === 'select') {
          const keys = f.matchKeys?.length ? f.matchKeys : [f.key];
          return keys.some((k) => item[k] === filterVal);
        }
        const keys = f.matchKeys?.length ? f.matchKeys : [f.key];
        const q = String(filterVal).toLowerCase();
        return keys.some((k) => String(item[k] ?? '').toLowerCase().includes(q));
      }),
    );
  }, [items, filterFields, filterValues]);

  const handleSave = () => {
    setSaving(true);
    save({ ...(editing ?? {}), ...(formValues as any) } as T)
      .then((res) => {
        if (res) {
          AppToastService.success(t('Elemento salvato con successo'));
          setShowForm(false);
          load();
        }
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (item: T) => {
    if (item.id == null) return;
    AntModal.confirm({
      title: t('Conferma eliminazione'),
      content: t('Sei sicuro di voler eliminare questo elemento?'),
      okText: t('Elimina'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: () =>
        remove(item.id!).then(() => {
          AppToastService.success(t('Elemento eliminato con successo'));
          load();
        }),
    });
  };

  const handleExportExcel = () => {
    const rows = filteredItems.map((item) => {
      const row: Record<string, any> = { ID: item.id ?? '' };
      fields.forEach((f) => {
        const v = item[f.key];
        if (f.type === 'boolean') {
          row[f.label] = v ? f.trueLabel ?? t('Sì') : f.falseLabel ?? t('No');
        } else if (f.type === 'select') {
          row[f.label] = f.options?.find((o) => o.value === v)?.label ?? v ?? '';
        } else {
          row[f.label] = v ?? '';
        }
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${fileName ?? 'export'}.xlsx`);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-3">
        <div className="d-flex flex-wrap gap-2 align-items-end">
          {(filterFields || []).map((f) => (
            <div key={f.key} style={{ minWidth: 220 }}>
              <label className="d-block text-muted mb-1" style={{ fontSize: '0.78rem', fontWeight: 600 }}>
                {f.label}
              </label>
              {f.type === 'select' ? (
                <Select
                  className="w-100"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                  placeholder={f.placeholder ?? f.label}
                  value={filterValues[f.key] as any}
                  onChange={(v) => setFilterValues((p) => ({ ...p, [f.key]: v }))}
                  options={f.options ?? []}
                />
              ) : f.type === 'boolean' ? (
                <Select
                  className="w-100"
                  allowClear
                  placeholder={f.placeholder ?? f.label}
                  value={filterValues[f.key] as any}
                  onChange={(v) => setFilterValues((p) => ({ ...p, [f.key]: v }))}
                  options={[
                    { value: true, label: f.trueLabel ?? t('Sì') },
                    { value: false, label: f.falseLabel ?? t('No') },
                  ]}
                />
              ) : (
                <Input
                  allowClear
                  placeholder={f.placeholder ?? f.label}
                  prefix={<i className="fa-solid fa-magnifying-glass text-muted" />}
                  value={(filterValues[f.key] as string | undefined) ?? ''}
                  onChange={(e) => setFilterValues((p) => ({ ...p, [f.key]: e.target.value }))}
                />
              )}
            </div>
          ))}
        </div>
        <div className="d-flex align-items-end gap-2">
          <LinksButton className="btn-sm btn btn-links-outline px-3" onClick={handleExportExcel}>
            <i className="fa-solid fa-file-excel me-2" />
            {t('Export Excel')}
          </LinksButton>
          <LinksButton className="btn btn-links btn-primary" onClick={openAdd}>
            <i className="fas fa-plus me-2" />
            {addLabel ?? t('Aggiungi')}
          </LinksButton>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <Spin />
        </div>
      ) : filteredItems.length === 0 ? (
        <NoData msg={emptyMessage ?? t('Nessun elemento')} />
      ) : (
        <div className="table-responsive" style={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table bordered hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: 80 }}>ID</th>
                {fields.map((f) => (
                  <th key={f.key}>{f.label}</th>
                ))}
                <th style={{ width: 100 }}>{t('Azioni')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id ?? Math.random()}>
                  <td>{item.id ?? '-'}</td>
                  {fields.map((f) => (
                    <td key={f.key}>{renderCell(item, f)}</td>
                  ))}
                  <td>
                    <div className="d-flex">
                      <button onClick={() => openEdit(item)} className="btn-icon">
                        <i className="icon-edit text-teal-links me-1" />
                      </button>
                      <button onClick={() => handleDelete(item)} className="btn-icon">
                        <i className="icon-delete" style={{ color: 'red' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <AntModal
        title={editing ? t('Modifica') : addLabel ?? t('Aggiungi')}
        open={showForm}
        width={modalWidth ?? 720}
        onCancel={() => setShowForm(false)}
        footer={[
          <LinksButton
            key="cancel"
            className="btn btn-links-outline me-2"
            onClick={() => setShowForm(false)}
          >
            {t('common.cancel')}
          </LinksButton>,
          <LinksButton
            key="save"
            className="btn-primary btn-links"
            disabled={missingRequired || saving}
            onClick={handleSave}
          >
            {saving ? <Spin size="small" className="me-2" /> : <i className="fas fa-check me-2" />}
            {t('common.save')}
          </LinksButton>,
        ]}
      >
        {fields.map((f) => (
          <div className="form-group mb-3" key={f.key}>
            <label className="d-block">
              {f.label}
              {f.required ? ' *' : ''}
            </label>
            {f.type === 'textarea' ? (
              <Input.TextArea
                placeholder={f.placeholder ?? f.label}
                value={(formValues[f.key] as string | undefined) ?? ''}
                onChange={(e) => setFormValues((p) => ({ ...p, [f.key]: e.target.value } as any))}
                rows={3}
              />
            ) : f.type === 'boolean' ? (
              <Switch
                checked={!!formValues[f.key]}
                checkedChildren={f.trueLabel ?? t('Sì')}
                unCheckedChildren={f.falseLabel ?? t('No')}
                onChange={(checked) => setFormValues((p) => ({ ...p, [f.key]: checked } as any))}
              />
            ) : f.type === 'select' ? (
              <Select
                className="w-100"
                allowClear
                showSearch
                placeholder={f.placeholder ?? f.label}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                value={formValues[f.key] as any}
                onChange={(v) => setFormValues((p) => ({ ...p, [f.key]: v } as any))}
                options={f.options ?? []}
              />
            ) : (
              <Input
                placeholder={f.placeholder ?? f.label}
                value={(formValues[f.key] as string | undefined) ?? ''}
                onChange={(e) => setFormValues((p) => ({ ...p, [f.key]: e.target.value } as any))}
              />
            )}
          </div>
        ))}
      </AntModal>
    </div>
  );
}

export default SimpleCrudTab;
